import { keyframes, styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { queries } from "../../shared/queries";
import { useAdminLandingView } from "../hooks/useAdminLandingView";
import {
  clearAdminViewSpace,
  useAdminSpaceView,
} from "../hooks/useAdminSpaceView";
import { useAdminViewSubject } from "../hooks/useAdminViewSubject";
import { mobileMedia } from "../hooks/useIsMobile";
import { useIsNarrow } from "../hooks/useIsNarrow";
import { useNavTransition } from "../hooks/useNavTransition";
import { useQuery } from "@rocicorp/zero/react";
import { useSidebarCollapsed } from "../hooks/useSidebarCollapsed";
import {
  getAdminViewReturnUrl,
  isAdminViewUrl,
  markAdminViewUrl,
  rememberAdminViewReturnUrl,
} from "../lib/adminViewUrls";
import {
  useMakeUrl,
  routes,
  useCurrentLocation,
  useRouteMatch,
} from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import { zIndexes } from "../lib/zIndexes";
import { AttentionStack } from "./AttentionStack";
import { BackPill, BackPillChip, BackPillIcon } from "./BackPill";
import { dragIndicatorShown } from "./DragIndicator";
import { Header } from "./Header";
import { Icon } from "./Icon";
import { Navigation } from "./Navigation";
import { MobileNavigation } from "./MobileNavigation";
import { NavigationFooter } from "./NavigationFooter";
import { PageBody } from "./PageBody";
import { ProfileMenu } from "./ProfileMenu";
import { SearchButton } from "./SearchButton";
import { SidebarResizeHandle } from "./SidebarResizeHandle";
import { SidebarToggle } from "./SidebarToggle";
import { readSidebarWidth, writeSidebarWidth } from "../lib/sidebarWidth";
import { MOTION_BASE_MS, MOTION_FAST_MS } from "./motion";
import { Text } from "./Text";
import { WorkspaceModeSwitch } from "./WorkspaceNavigation";

const Container = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--surface-secondary);
  width: 100%;
  height: 100%;
  &:has([data-sidebar-region]:hover)
    [data-sidebar-toggle]
    [data-drag-indicator] {
    ${dragIndicatorShown}
  }
  /* Resizing works the far edge, so the collapse handle steps out of the way. */
  &:has(
      [data-sidebar-resize]:hover,
      [data-sidebar-resize][data-dragging="true"]
    )
    [data-sidebar-toggle]
    [data-drag-indicator] {
    transform: translateX(-12px);
  }
`;

const SHELL_PADDING = 12;
const NAVIGATION_HEADER_HEIGHT = 56;
const HEADER_OVERLAP = NAVIGATION_HEADER_HEIGHT - SHELL_PADDING;

const Layout = styled("div")`
  display: flex;
  flex: 1;
  overflow: hidden;
  padding-top: 1px;
  padding-bottom: ${SHELL_PADDING}px;
  padding-right: ${SHELL_PADDING}px;
  flex-direction: row;
  height: 100%;
  position: relative;
  &[data-full-height="true"],
  &[data-header-overlay="true"] {
    margin-top: -${NAVIGATION_HEADER_HEIGHT}px;
    padding-top: ${SHELL_PADDING}px;
  }
  &[data-header-hidden="true"] {
    padding-top: ${SHELL_PADDING}px;
    z-index: 1;
  }
  & > :not(:first-child) {
    z-index: 1;
  }
`;

const MobileBody = styled("div")`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: max(8px, env(safe-area-inset-top))
    max(8px, env(safe-area-inset-right)) max(8px, env(safe-area-inset-bottom))
    max(8px, env(safe-area-inset-left));

  ${mobileMedia} {
    padding: 0;

    & [data-page-surface] {
      border: none;
      border-radius: 0;
      outline: none;
      box-shadow: none;
    }
  }
`;

const SIDEBAR_COLLAPSED_WIDTH = 12;

const sidebarWidthEasing = `${MOTION_BASE_MS}ms var(--ease-out)`;
const sidebarWidthTransition = `width ${sidebarWidthEasing}`;

const LeftSidebar = styled("div")`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: visible;
  position: relative;
  margin-top: -1px;
  width: var(--sidebar-width);
  transition: ${sidebarWidthTransition};
  &[data-resizing="true"] {
    transition: none;
  }
  &[data-collapsed="true"] {
    width: ${SIDEBAR_COLLAPSED_WIDTH}px;
  }
  &[data-full-height="true"] {
    margin-top: ${HEADER_OVERLAP + SHELL_PADDING}px;
  }
`;

const NavigationBody = styled("div")`
  display: flex;
  flex: 1;
  flex-direction: column;
  flex-shrink: 0;
  min-height: 0;
  width: var(--sidebar-width);
  opacity: 1;
  transform: none;
  transition:
    opacity ${MOTION_BASE_MS}ms var(--ease-out),
    transform ${MOTION_BASE_MS}ms var(--ease-out),
    ${sidebarWidthTransition};
  [data-resizing="true"] > & {
    transition:
      opacity ${MOTION_BASE_MS}ms var(--ease-out),
      transform ${MOTION_BASE_MS}ms var(--ease-out);
  }
  &[data-leaving="true"] {
    opacity: 0;
    transform: translateX(-32px);
  }
`;

const Separator = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--border-alpha);
  width: 16px;
  height: 16px;
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

// Each page has its own backdrop, so the flyout’s state outlives them to stay open while you move between pages from it.
let flyoutOpen = false;

const TemporaryNavigationContainer = styled("div")`
  box-shadow:
    var(--elevation-high),
    inset -0.5px 0 var(--border-alpha);
  position: absolute;
  z-index: ${zIndexes.navigation};
  top: 0;
  bottom: 0;
  width: var(--sidebar-width);
  background: var(--surface-secondary);
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
  &[data-state="entering"] {
    animation: ${slideIn} var(--motion-base) var(--ease-out);
  }
  &[data-state="exiting"] {
    animation: ${slideOut} ${MOTION_FAST_MS}ms var(--ease-in) forwards;
  }
`;

type Props = {
  children: React.ReactNode;
  middle?: React.ReactNode;
  permanentNavigation?: boolean;
  rightPrefix?: React.ReactNode;
  navigation?: React.ReactNode;
  inChat?: boolean;
  // Shown in place of the Home and Chat switch, sliding in as a deeper sidebar does.
  headerContent?: React.ReactNode;
  navigationHidden?: boolean;
};

export function Backdrop({
  children,
  middle,
  permanentNavigation: permanentNavigationProp,
  rightPrefix,
  navigation,
  inChat = false,
  headerContent,
  navigationHidden = false,
}: Props) {
  const makeUrl = useMakeUrl();
  const isMobile = useIsNarrow(768);
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed();

  const effectivelyCollapsed = isMobile || sidebarCollapsed || navigationHidden;
  const [sidebarWidth, setSidebarWidth] = useState(readSidebarWidth);
  const [resizingSidebar, setResizingSidebar] = useState(false);
  const [displayCollapsed, setDisplayCollapsed] =
    useState(effectivelyCollapsed);
  const [restoredFlyout] = useState(
    () => flyoutOpen && effectivelyCollapsed && !isMobile
  );
  const [tempNavMounted, setTempNavMounted] = useState(restoredFlyout);
  useEffect(() => {
    if (effectivelyCollapsed && !displayCollapsed) {
      const t = setTimeout(() => setDisplayCollapsed(true), MOTION_BASE_MS);
      return () => clearTimeout(t);
    }
    if (!effectivelyCollapsed && displayCollapsed) {
      if (tempNavMounted) {
        const t = setTimeout(() => setDisplayCollapsed(false), MOTION_BASE_MS);
        return () => clearTimeout(t);
      }
      // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: snaps the collapsed state when there is no temp nav to animate, outside the sidebar transition timer
      setDisplayCollapsed(false);
    }
  }, [effectivelyCollapsed, displayCollapsed, tempNavMounted]);
  const morphingFromTemp = !effectivelyCollapsed && displayCollapsed;
  const permanentNavigation = permanentNavigationProp && !displayCollapsed;
  const [user] = useQuery(queries.currentUser());
  const adminSubject = useAdminViewSubject();
  const adminSpace = useAdminSpaceView();
  const adminLanding = useAdminLandingView();
  const [adminUsers] = useQuery(adminSubject ? queries.allUsers() : null);
  const adminUser = adminSubject
    ? adminUsers?.find((u) => u.id === adminSubject)
    : undefined;
  const adminViewActive = !!adminSubject || !!adminSpace || adminLanding;
  const currentLocation = useCurrentLocation();
  useEffect(() => {
    if (adminViewActive) {
      markAdminViewUrl(currentLocation);
    } else {
      rememberAdminViewReturnUrl(currentLocation);
      clearAdminViewSpace();
    }
  }, [adminViewActive, currentLocation]);
  // A cold load of an admin URL renders before grants sync, so the effect
  // below can remember the admin URL itself as the return target — filter
  // those out here, where the URL has since been marked.
  const adminViewReturnUrl = getAdminViewReturnUrl();
  const [onSettings] = useRouteMatch(routes.settings);
  const [onChats] = useRouteMatch(routes.chats);
  const navigationKind = onSettings
    ? "settings"
    : adminViewActive
      ? "admin"
      : "workspace";
  const workflowHeader = !!headerContent && !onSettings;
  const headerKind = workflowHeader ? "workflow" : navigationKind;
  const mountedHeaderEnter = useNavTransition(headerKind, "header");
  // The chat page stays mounted while its workflow navigation opens and closes, so the header slides on the switch itself.
  const [shownHeaderKind, setShownHeaderKind] = useState(headerKind);
  const [switchedHeaderEnter, setSwitchedHeaderEnter] = useState<
    "left" | "right" | undefined
  >(undefined);
  if (headerKind !== shownHeaderKind) {
    setShownHeaderKind(headerKind);
    setSwitchedHeaderEnter(workflowHeader ? "right" : "left");
  }
  const headerEnter = switchedHeaderEnter ?? mountedHeaderEnter;
  // Opening a workflow’s navigation goes deeper, so the page slides the same way as the sidebar.
  const bodyKindEnter = useNavTransition(
    workflowHeader
      ? "workflow"
      : (onChats || inChat) && !adminViewActive
        ? "chat"
        : navigationKind,
    "body"
  );
  // Toggling the chat page’s workflow navigation in place leaves the body unchanged, so it slides only on arrival.
  const [bodyEnter] = useState(bodyKindEnter);
  const showNavigationFooter = permanentNavigationProp || tempNavMounted;
  const fullHeightNavigation =
    showNavigationFooter &&
    permanentNavigation &&
    !effectivelyCollapsed &&
    !isMobile &&
    !rightPrefix &&
    !middle;
  const sidebarFullHeight =
    showNavigationFooter &&
    permanentNavigationProp &&
    !displayCollapsed &&
    !isMobile &&
    !rightPrefix &&
    !middle;
  const adminViewBackUrl = !adminLanding
    ? makeUrl(routes.watchtower)
    : ((adminViewReturnUrl && !isAdminViewUrl(adminViewReturnUrl)
        ? adminViewReturnUrl
        : null) ?? makeUrl(routes.settings.spaces));
  const [showingTemporaryNavigation, setShowingTemporaryNavigation] =
    useState(restoredFlyout);
  useEffect(() => {
    flyoutOpen = showingTemporaryNavigation;
  }, [showingTemporaryNavigation]);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  // A workflow’s navigation is portaled into the flyout, and React counts moving onto it as leaving, so listen on the DOM.
  useEffect(() => {
    const flyout = flyoutRef.current;
    if (!tempNavMounted || !flyout) return;
    // The handle floats over the flyout’s edge, so moving onto it isn’t leaving.
    const close = (event: MouseEvent) => {
      if (
        event.relatedTarget instanceof Node &&
        toggleRef.current?.contains(event.relatedTarget)
      )
        return;
      setShowingTemporaryNavigation(false);
    };
    flyout.addEventListener("mouseleave", close);
    return () => flyout.removeEventListener("mouseleave", close);
  }, [tempNavMounted]);
  // A flyout carried over from the last page closes as soon as the pointer moves anywhere outside it.
  useEffect(() => {
    if (!restoredFlyout) return;
    const onPointerMove = (event: PointerEvent) => {
      document.removeEventListener("pointermove", onPointerMove);
      const target = event.target as Node;
      if (
        !flyoutRef.current?.contains(target) &&
        !toggleRef.current?.contains(target)
      )
        setShowingTemporaryNavigation(false);
    };
    document.addEventListener("pointermove", onPointerMove);
    return () => document.removeEventListener("pointermove", onPointerMove);
  }, [restoredFlyout]);
  useEffect(() => {
    if (isMobile) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        !event.repeat &&
        event.key === "\\"
      ) {
        event.preventDefault();
        setSidebarCollapsed(!sidebarCollapsed);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobile, sidebarCollapsed, setSidebarCollapsed]);

  const navigationContent = navigation ?? <Navigation />;
  const collapsingNavigation =
    permanentNavigationProp &&
    !isMobile &&
    effectivelyCollapsed &&
    !tempNavMounted;
  const headerHidden = isMobile || (collapsingNavigation && displayCollapsed);
  const headerLeaving = collapsingNavigation && !displayCollapsed;
  const headerEntering =
    permanentNavigationProp &&
    !isMobile &&
    !effectivelyCollapsed &&
    displayCollapsed &&
    !tempNavMounted;
  const overlayTemporaryHeader =
    permanentNavigationProp &&
    !isMobile &&
    !permanentNavigation &&
    !headerHidden;

  const onLogoHover = (hovering: boolean) => {
    if (isMobile) return;
    if (!permanentNavigation) {
      setShowingTemporaryNavigation(hovering);
    }
  };

  const gutterHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelGutterHover = () => {
    if (gutterHoverTimer.current) {
      clearTimeout(gutterHoverTimer.current);
      gutterHoverTimer.current = null;
    }
  };
  const scheduleGutterHover = () => {
    cancelGutterHover();
    gutterHoverTimer.current = setTimeout(() => {
      setShowingTemporaryNavigation(true);
    }, 250);
  };
  const toggleSidebar = () => {
    cancelGutterHover();
    setShowingTemporaryNavigation(false);
    setSidebarCollapsed(!effectivelyCollapsed);
  };
  useEffect(() => {
    return () => {
      if (gutterHoverTimer.current) clearTimeout(gutterHoverTimer.current);
    };
  }, []);
  useEffect(() => {
    if (permanentNavigation) {
      // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: hides temporary navigation and clears the pending gutter-hover timer when nav becomes permanent
      setShowingTemporaryNavigation(false);
      if (gutterHoverTimer.current) {
        clearTimeout(gutterHoverTimer.current);
        gutterHoverTimer.current = null;
      }
    }
  }, [permanentNavigation]);

  const wantsTempNav =
    (showingTemporaryNavigation || morphingFromTemp) && !permanentNavigation;
  const [tempNavState, setTempNavState] = useState<
    "shown" | "entering" | "exiting"
  >(restoredFlyout ? "shown" : "entering");
  useEffect(() => {
    if (wantsTempNav) {
      // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: mounts the temp nav to drive its enter/exit transition against the exit timeout below
      setTempNavMounted(true);
      setTempNavState((state) => (state === "shown" ? state : "entering"));
      return;
    }
    if (!tempNavMounted) return;
    if (permanentNavigation) {
      setTempNavMounted(false);
      return;
    }
    setTempNavState("exiting");
    const t = setTimeout(() => setTempNavMounted(false), MOTION_FAST_MS);
    return () => clearTimeout(t);
  }, [wantsTempNav, tempNavMounted, permanentNavigation]);

  const navigationHeaderInFlyout = tempNavMounted && !permanentNavigation;
  const workspaceSwitch = (
    <WorkspaceModeSwitch
      navigationOpen={showingTemporaryNavigation}
      onActiveModePress={
        !permanentNavigation
          ? () =>
              setShowingTemporaryNavigation((open) => (isMobile ? !open : true))
          : undefined
      }
    />
  );
  const watchtowerPill = (
    <BackPill aria-label="Back from Watchtower" to={adminViewBackUrl}>
      <BackPillIcon>
        <Icon name="tower-control" size={16} />
      </BackPillIcon>
      <Text>Watchtower</Text>
      {adminUser ? (
        <BackPillChip>
          <Icon name="circle-user-round" size={10} style={{ flexShrink: 0 }} />
          <Text variant="text-small-md" ellipsis>
            {userDisplayName(adminUser)}
          </Text>
        </BackPillChip>
      ) : (
        adminSpace && (
          <BackPillChip>
            <Icon name="hash" size={10} style={{ flexShrink: 0 }} />
            <Text variant="text-small-md" ellipsis>
              {adminSpace.name}
            </Text>
          </BackPillChip>
        )
      )}
    </BackPill>
  );
  const pill = headerContent ?? (adminViewActive ? watchtowerPill : null);
  const navigationTitle = (
    <div
      onMouseEnter={isMobile ? undefined : () => onLogoHover(true)}
      style={{
        position: tempNavMounted || sidebarFullHeight ? "relative" : undefined,
        zIndex: tempNavMounted
          ? zIndexes.navigation + 1
          : sidebarFullHeight
            ? 2
            : undefined,
        display: "flex",
        alignItems: "center",
        gap: 4,
        height: 32,
        width: `calc(var(--sidebar-width) - ${SHELL_PADDING * (sidebarFullHeight ? 4 : 2)}px)`,
        transition: resizingSidebar ? undefined : sidebarWidthTransition,
        margin: sidebarFullHeight ? `0 ${SHELL_PADDING}px` : undefined,
        flexShrink: 0,
        transform: sidebarFullHeight
          ? `translateY(${SHELL_PADDING}px)`
          : undefined,
      }}
    >
      {pill ?? workspaceSwitch}
    </div>
  );

  return (
    <Container
      style={{ "--sidebar-width": `${sidebarWidth}px` } as React.CSSProperties}
    >
      <Header
        mobile={isMobile}
        hidden={headerHidden}
        leaving={headerLeaving}
        entering={headerEntering}
        navEnter={headerEnter}
        left={
          navigationHeaderInFlyout ? (
            <div
              aria-hidden
              style={{
                height: 32,
                width: `calc(var(--sidebar-width) - ${SHELL_PADDING * 2}px)`,
                transition: resizingSidebar
                  ? undefined
                  : sidebarWidthTransition,
              }}
            />
          ) : (
            navigationTitle
          )
        }
        middle={middle}
        right={
          <div
            style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              anchorName: "--toast-header",
            }}
          >
            {rightPrefix}
            {rightPrefix && !isMobile && !showNavigationFooter && (
              <Separator>/</Separator>
            )}
            {!isMobile && !showNavigationFooter && <SearchButton />}
            {!isMobile && !showNavigationFooter && <AttentionStack />}
            {user && !showNavigationFooter && <ProfileMenu user={user} />}
          </div>
        }
      />
      {permanentNavigationProp && !isMobile ? (
        <Layout
          data-full-height={fullHeightNavigation}
          data-header-overlay={overlayTemporaryHeader}
          data-header-hidden={collapsingNavigation}
        >
          <LeftSidebar
            data-sidebar-region
            data-collapsed={effectivelyCollapsed}
            data-full-height={sidebarFullHeight}
            data-resizing={resizingSidebar}
          >
            {/* Keep footer shortcuts active without making collapsed links focusable. */}
            {(!displayCollapsed || !tempNavMounted) && (
              <NavigationBody
                data-leaving={effectivelyCollapsed || displayCollapsed}
                inert={effectivelyCollapsed || displayCollapsed}
                style={{
                  visibility: displayCollapsed ? "hidden" : undefined,
                }}
              >
                {navigationContent}
                {showNavigationFooter && (
                  <NavigationFooter showActivity={!tempNavMounted} />
                )}
              </NavigationBody>
            )}
          </LeftSidebar>
          <PageBody enter={bodyEnter}>{children}</PageBody>
        </Layout>
      ) : permanentNavigationProp && isMobile ? (
        <MobileBody>
          <PageBody enter={bodyEnter}>{children}</PageBody>
        </MobileBody>
      ) : (
        children
      )}
      {permanentNavigationProp && !isMobile && !effectivelyCollapsed && (
        <SidebarResizeHandle
          width={sidebarWidth}
          dragging={resizingSidebar}
          onResize={(width) => {
            setResizingSidebar(true);
            setSidebarWidth(width);
          }}
          onResizeEnd={(width) => {
            setResizingSidebar(false);
            setSidebarWidth(width);
            writeSidebarWidth(width);
          }}
          style={{
            zIndex: zIndexes.navigation,
            transition: resizingSidebar
              ? undefined
              : `left ${sidebarWidthEasing}`,
          }}
        />
      )}
      {/* One handle serves the gutter and the flyout, so it stays put while the flyout slides in. */}
      {permanentNavigationProp && !isMobile && !navigationHidden && (
        <SidebarToggle
          ref={toggleRef}
          collapsed={effectivelyCollapsed}
          onToggle={toggleSidebar}
          onMouseEnter={effectivelyCollapsed ? scheduleGutterHover : undefined}
          onMouseLeave={(event) => {
            cancelGutterHover();
            if (
              !(event.relatedTarget instanceof Node) ||
              !flyoutRef.current?.contains(event.relatedTarget)
            )
              setShowingTemporaryNavigation(false);
          }}
          style={{
            zIndex: zIndexes.navigation + 1,
          }}
        />
      )}
      {isMobile && (
        <MobileNavigation header={pill ?? <WorkspaceModeSwitch />}>
          {navigationContent}
          {showNavigationFooter && <NavigationFooter />}
        </MobileNavigation>
      )}
      {!isMobile && tempNavMounted && !permanentNavigation && (
        <TemporaryNavigationContainer
          ref={flyoutRef}
          data-sidebar-region
          data-popover-host
          data-state={tempNavState}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              flexShrink: 0,
              padding: `${SHELL_PADDING * 2}px ${SHELL_PADDING * 2}px ${SHELL_PADDING}px`,
            }}
          >
            {pill ?? workspaceSwitch}
          </div>
          {navigationContent}
          {showNavigationFooter && <NavigationFooter />}
        </TemporaryNavigationContainer>
      )}
    </Container>
  );
}
